import React from "react";
import "../FlowerField.css";
import { FLOWER_R, bankScenery, pondPath } from "./connectTheDots";

/**
 * PondArt — the cut-paper drawing for Connect the Dots, in board units (the
 * board is one W x H SVG viewBox, short side 100). PondScenery is the backdrop:
 * water, a grassy bank laid over it like a sheet of paper with a pond-shaped
 * hole, and the bank's grass tufts and cattails. Blossom is one numbered
 * floating flower. All colours resolve through theme.css variables in
 * ConnectTheDotsForm.css; nothing here hard-codes a hex.
 */

// Bloom name -> class that sets --petal-fill / --petal-fold. Three reuse the
// home meadow's classes (FlowerField.css); the cream water-lily is the pond's own.
export const BLOOM_CLASS = {
  marigold: "flower-field__bloom--marigold",
  berry: "flower-field__bloom--berry",
  butter: "flower-field__bloom--butter",
  lily: "ctd-bloom--lily",
};

const r2 = (v) => Math.round(v * 100) / 100;

// Faint ripples on the open water: [u, v, rx, ry], where (u, v) is a spot on
// the open-water oval (-1..1 across each axis). Each is drawn as the top arc of
// an ellipse with a smaller arc nested inside it.
const RIPPLES = [
  [-0.5, 0.35, 6, 1.6],
  [0.45, -0.45, 5, 1.3],
  [0.3, 0.7, 3.5, 1],
  [-0.35, -0.55, 3, 0.9],
  [0.7, 0.2, 4, 1.1],
  [-0.8, -0.1, 3.5, 1],
  [0.05, 0.1, 2.5, 0.8],
];
const rippleArc = (cx, cy, rx, ry) =>
  `M${r2(cx - rx)},${cy} A${rx},${ry} 0 0 1 ${r2(cx + rx)},${cy}`;

// A grass tuft growing up from (0, 0): five filled cut-paper blades fanning
// out, each bowed slightly outward. [lean in degrees from upright, length]
const TUFT_BLADES = [
  [-40, 2.6],
  [-17, 3.8],
  [3, 4.6],
  [21, 3.4],
  [42, 2.4],
];
const TUFT = TUFT_BLADES.map(([deg, len]) => {
  const a = (deg * Math.PI) / 180;
  const tx = Math.sin(a) * len;
  const ty = -Math.cos(a) * len;
  // (px, py) is perpendicular to the blade; the base is 0.9 wide.
  const px = Math.cos(a);
  const py = Math.sin(a);
  const mx = tx * 0.45 + px * 0.25;
  const my = ty * 0.55 + py * 0.25;
  return (
    `M${r2(-px * 0.45)},${r2(-py * 0.45)} ` +
    `Q${r2(mx - px * 0.4)},${r2(my - py * 0.4)} ${r2(tx)},${r2(ty)} ` +
    `Q${r2(mx + px * 0.4)},${r2(my + py * 0.4)} ${r2(px * 0.45)},${r2(py * 0.45)} Z`
  );
}).join(" ");

function Cattails({ x, y, height }) {
  // Three stems of different heights leaning out from one ground point, each
  // tipped with a brown seed head, plus two long blade leaves.
  const stems = [
    { dx: -1.6, h: height * 0.82 },
    { dx: 0.3, h: height },
    { dx: 2, h: height * 0.7 },
  ];
  return (
    <g transform={`translate(${x} ${y})`}>
      <path className="ctd-reed__leaf" d={`M-0.4,0 Q-3.6,${-height * 0.4} -4.4,${-height * 0.75} Q-2.4,${-height * 0.45} 0.4,0 Z`} />
      <path className="ctd-reed__leaf ctd-reed__leaf--dark" d={`M0.2,0 Q3.4,${-height * 0.3} 4.6,${-height * 0.6} Q2.6,${-height * 0.32} 0.9,0 Z`} />
      {stems.map(({ dx, h }, i) => (
        <g key={i}>
          <path className="ctd-reed__stem" d={`M0,0 Q${r2(dx * 0.4)},${r2(-h * 0.5)} ${dx},${r2(-h)}`} />
          <rect
            className="ctd-reed__head"
            x={dx - 0.75}
            y={r2(-h + 0.6)}
            width="1.5"
            height="3.4"
            rx="0.75"
          />
        </g>
      ))}
    </g>
  );
}

function PondSceneryBase({ pond }) {
  const { tufts, reeds, reedHeight } = bankScenery(pond);
  const shore = pondPath(pond);
  const { W, H } = pond;
  return (
    <g aria-hidden="true">
      <defs>
        <radialGradient id="ctd-water" cx="50%" cy="45%" r="55%">
          <stop offset="0%" className="ctd-water__stop--centre" />
          <stop offset="100%" className="ctd-water__stop--edge" />
        </radialGradient>
        <clipPath id="ctd-water-clip">
          <path d={shore} />
        </clipPath>
      </defs>

      <path className="ctd-water" d={shore} fill="url(#ctd-water)" />
      <path className="ctd-shallows" d={shore} />
      {/* The bank's paper lift: its shadow falls onto the water just inside the
          shore (strokes of the shoreline nudged down, clipped to the water) — a
          crisp edge plus a wider, fainter drop. */}
      <g clipPath="url(#ctd-water-clip)">
        <path className="ctd-shore-shadow ctd-shore-shadow--soft" d={shore} transform="translate(0 1)" />
        <path className="ctd-shore-shadow" d={shore} transform="translate(0 0.5)" />
      </g>
      {RIPPLES.map(([u, v, rx, ry], i) => {
        const cx = r2(pond.cx + u * pond.ax);
        const cy = r2(pond.cy + v * pond.ay);
        return (
          <path
            key={i}
            className="ctd-ripple"
            d={`${rippleArc(cx, cy, rx, ry)} ${rippleArc(cx, r2(cy + ry * 0.7), rx * 0.55, ry * 0.5)}`}
          />
        );
      })}

      {/* The bank: a grass sheet with the pond cut out of it. It reaches well
          past the board on every side, so its edge can never show, even if the
          drawing is ever scaled into a board of a different shape. */}
      <path
        className="ctd-bank"
        d={`M${-2 * W},${-2 * H} H${3 * W} V${3 * H} H${-2 * W} Z ${shore}`}
        fillRule="evenodd"
      />
      <path className="ctd-shore" d={shore} />

      {tufts.map((t, i) => (
        <path
          key={i}
          className={`ctd-tuft${t.dark ? " ctd-tuft--dark" : ""}`}
          d={TUFT}
          transform={`translate(${t.x} ${t.y}) scale(${t.flip} 1)`}
        />
      ))}
      {reeds.map((r, i) => (
        <Cattails key={i} x={r.x} y={r.y} height={reedHeight} />
      ))}
    </g>
  );
}

// The backdrop only changes with the board's size.
export const PondScenery = React.memo(PondSceneryBase);

// A petal ring as one filled path: `count` ellipses (half-width `w`, half-length
// `h`) whose long axes point out from the centre at distance `dist`. Same
// construction as the home meadow's ring flowers (flowerScatter.js).
const petalRing = (count, dist, w, h, offsetDeg) => {
  let d = "";
  for (let i = 0; i < count; i++) {
    const a = offsetDeg + (i * 360) / count;
    const rad = (a * Math.PI) / 180;
    const px = Math.sin(rad) * dist;
    const py = -Math.cos(rad) * dist;
    const ex = Math.cos(rad) * w;
    const ey = Math.sin(rad) * w;
    const arc = `A${w},${h} ${r2(a)} 1 0 `;
    d +=
      `M${r2(px - ex)},${r2(py - ey)}` +
      arc + `${r2(px + ex)},${r2(py + ey)}` +
      arc + `${r2(px - ex)},${r2(py - ey)}`;
  }
  return d;
};

// Petal paths depend only on the petal count, so build each once.
const ringCache = {};
const blossomRings = (petals) => {
  if (!ringCache[petals]) {
    ringCache[petals] = {
      back: petalRing(petals, 3.05, 1.6, 2.6, 180 / petals),
      front: petalRing(petals, 2.7, 1.5, 2.35, 0),
    };
  }
  return ringCache[petals];
};

// Kept generous relative to the petals so the number stays easy to read.
const CENTRE_R = 3;

/**
 * One numbered floating blossom, drawn around (0, 0); the caller positions it.
 * Layers, bottom up: a water ring, its shadow on the water, two leaves peeking
 * out, a back ring of petals in the darker fold colour, the front ring, then a
 * cream centre disc carrying the number (kept upright — only the petals turn
 * and sway). `joined` / `wrong` add ctd-blossom--joined / --wrong.
 */
export const Blossom = React.forwardRef(function Blossom(
  { blossom, x, y, joined, wrong, onTap },
  ref,
) {
  const { id, bloom, petals, turn } = blossom;
  const rings = blossomRings(petals);
  const label = String(id + 1);
  const className = [
    "ctd-blossom",
    BLOOM_CLASS[bloom],
    joined ? "ctd-blossom--joined" : "",
    wrong ? "ctd-blossom--wrong" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Mouse/touch taps act on press, so a drifting flower can't slip out from
  // under the finger before release. Keyboard and assistive-tech activation
  // arrive as a click with detail 0 (a real pointer click has detail >= 1), or
  // as Enter/Space on the focused flower.
  const handlePointerDown = (e) => {
    if (e.button === 0 || e.button === undefined) onTap(id);
  };
  const handleClick = (e) => {
    if (e.detail === 0) onTap(id);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onTap(id);
    }
  };

  return (
    <g
      ref={ref}
      className={className}
      transform={`translate(${r2(x)} ${r2(y)})`}
      role="button"
      tabIndex={0}
      aria-label={`Flower ${label}`}
      aria-pressed={joined}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <circle className="ctd-blossom__ring" r={FLOWER_R + 1.1} />
      <g className="ctd-blossom__art">
        <g
          className="ctd-blossom__sway"
          style={{ animationDelay: `${-((turn / 360) * 7).toFixed(2)}s` }}
        >
          {/* The shadow falls down-right on the water whichever way the petals turn. */}
          <g transform="translate(0.45 0.7)">
            <path className="ctd-blossom__shadow" d={rings.back} transform={`rotate(${turn})`} />
          </g>
          <g transform={`rotate(${turn})`}>
            <ellipse className="ctd-blossom__leaf" cx="0" cy="-3.7" rx="1.6" ry="2.55" transform="rotate(130)" />
            <ellipse className="ctd-blossom__leaf ctd-blossom__leaf--dark" cx="0" cy="-3.55" rx="1.35" ry="2.3" transform="rotate(205)" />
            <path className="ctd-blossom__back" d={rings.back} />
            <path className="ctd-blossom__petals" d={rings.front} />
          </g>
        </g>
        <circle className="ctd-blossom__centre" r={CENTRE_R} />
        <text
          className={`ctd-blossom__number${label.length > 1 ? " ctd-blossom__number--small" : ""}`}
          x="0"
          y="0"
        >
          {label}
        </text>
      </g>
      <circle className="ctd-blossom__alert" r={FLOWER_R + 0.6} />
      <circle className="ctd-blossom__focus" r={FLOWER_R + 1.6} />
      {/* Transparent disc so the gaps between petals still catch the tap. */}
      <circle className="ctd-blossom__hit" r={FLOWER_R + 0.5} />
    </g>
  );
});
